/**
 * The apply form.
 *
 * Three things here are the product rather than the plumbing:
 *
 * 1. **The visibility toggle is explained, not just offered.** Captains here
 *    are often deliberately AIS-dark. A bare "Share this application" switch
 *    invites a guess about what it discloses; the copy states exactly who can
 *    see what, and the default comes from the vessel rather than from a
 *    constant.
 *
 * 2. **The position snaps to the registry.** A pin 40 m from a registered
 *    anchorage means that anchorage, not a new one -- otherwise the registry
 *    accumulates a near-duplicate every time someone applies.
 *
 * 3. **Submitting works offline.** The application is queued with a
 *    `client_ref` generated before the first attempt, so a retry after a
 *    dropped connection resolves to the same application rather than filing a
 *    second one.
 */

import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Field, Notice, formatPosition } from '@abg/ui'
import { fromLocalInputValue, nextQuarterHour, toLocalInputValue } from '@abg/ui/time'
import { api, ApiError, OfflineError } from '../lib/api'
import { useAuth } from '../lib/auth'
import { looksNorwegian } from '../lib/geo'
import { enqueue, newClientRef } from '../lib/offline'
import { DateTimeField } from '../components/DateTimeField'
import { PositionEntry } from '../components/PositionEntry'
import type { Anchorage, Application } from '../lib/types'

export function ApplyPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [params] = useSearchParams()

  const [lat, setLat] = useState<number | null>(
    params.get('lat') ? Number(params.get('lat')) : null,
  )
  const [lon, setLon] = useState<number | null>(
    params.get('lon') ? Number(params.get('lon')) : null,
  )
  const [anchorage, setAnchorage] = useState<Anchorage | null>(null)
  const [checkingSnap, setCheckingSnap] = useState(false)

  const defaults = useMemo(() => {
    const start = nextQuarterHour(new Date(Date.now() + 60 * 60 * 1000))
    const end = new Date(start.getTime() + 24 * 60 * 60 * 1000)
    return { start, end }
  }, [])

  const [windowStart, setWindowStart] = useState(toLocalInputValue(defaults.start))
  const [windowEnd, setWindowEnd] = useState(toLocalInputValue(defaults.end))
  const [notes, setNotes] = useState('')
  // Seeded from the vessel's own default, so the switch always shows what
  // would actually be filed. Until it is touched we send null and let the
  // server apply that default itself -- one source of truth, not two.
  const [visible, setVisible] = useState(user?.vessel_visibility_default === 'visible')
  const [visibilityTouched, setVisibilityTouched] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Pre-selected anchorage from the map.
  useEffect(() => {
    const id = params.get('anchorage')
    if (!id) return
    void api
      .get<Anchorage>(`/api/anchorages/${id}`)
      .then((found) => {
        setAnchorage(found)
        setLat(found.lat)
        setLon(found.lon)
      })
      .catch(() => setError('Could not load that anchorage. Place a pin on the chart instead.'))
  }, [params])

  // If /auth/me resolves after first paint, re-seed the switch -- but never
  // over a choice the captain has already made.
  useEffect(() => {
    if (!visibilityTouched && user) {
      setVisible(user.vessel_visibility_default === 'visible')
    }
  }, [user, visibilityTouched])

  // Snap a free position to a nearby registry entry.
  useEffect(() => {
    if (lat === null || lon === null || params.get('anchorage')) return
    let cancelled = false
    setCheckingSnap(true)
    void api
      .get<Anchorage | null>(`/api/anchorages/nearest?lat=${lat}&lon=${lon}`)
      .then((near) => {
        if (cancelled) return
        setAnchorage(near)
        if (near?.status === 'blocked') {
          // The registry used to hide these from the snap, which meant a pin
          // dropped inside a bay we have marked blocked came back as an
          // unregistered position and went through. Now it snaps, and the one
          // record that says "not allowed here" is the one the captain sees.
          setInfo(null)
        } else if (near) {
          setInfo(
            `This is within the registered anchorage “${near.name ?? 'unnamed'}”. ` +
              'Your application will be filed against it.',
          )
        }
      })
      .catch(() => undefined)
      .finally(() => !cancelled && setCheckingSnap(false))
    return () => {
      cancelled = true
    }
  }, [lat, lon, params])

  const submit = async (event: FormEvent, asDraft: boolean) => {
    event.preventDefault()
    setError(null)

    if (lat === null || lon === null) {
      setError('Pick a position first — use the chart or "Use my position".')
      return
    }
    // The server refuses a blocked anchorage too (services/applications.create).
    // This is here so the refusal arrives before the captain has filled in a
    // window, and in words rather than as a 409.
    if (anchorage?.status === 'blocked') {
      setError(
        `Anchoring is not allowed at ${anchorage.name ?? 'this position'}. ` +
          'Move the pin, or talk to us about an alternative.',
      )
      return
    }
    const startIso = fromLocalInputValue(windowStart)
    const endIso = fromLocalInputValue(windowEnd)
    if (!startIso || !endIso) {
      setError('Enter both a start and an end time.')
      return
    }
    if (new Date(endIso) <= new Date(startIso)) {
      setError('The end of the window must be after the start.')
      return
    }
    if (!looksNorwegian(lat, lon)) {
      setError(
        'That position is outside the area we cover. Check the pin, or contact your agent.',
      )
      return
    }

    const body = {
      lat,
      lon,
      window_start: startIso,
      window_end: endIso,
      anchorage_id: anchorage?.id ?? null,
      visibility: visibilityTouched ? (visible ? 'visible' : 'hidden') : null,
      captain_notes: notes.trim() || null,
      submit: !asDraft,
      // Minted before the first attempt, so a retry is idempotent server-side.
      client_ref: newClientRef(),
    }

    setBusy(true)
    try {
      const created = await api.post<Application>('/api/applications', body)
      navigate(`/applications/${created.id}`, { replace: true })
    } catch (caught) {
      if (caught instanceof OfflineError) {
        await enqueue({
          id: body.client_ref,
          path: '/api/applications',
          method: 'POST',
          body,
          label: `Application at ${formatPosition(lat, lon)}`,
        })
        navigate('/applications', {
          replace: true,
          state: { queued: true },
        })
        return
      }
      setError(caught instanceof ApiError ? caught.message : 'Could not file the application.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="page" onSubmit={(e) => submit(e, false)}>
      <header className="page-header">
        <h1>New application</h1>
        <span className="subtitle">
          {user?.vessel_name ?? 'Your vessel'} · we file this with the local harbour authority
        </span>
      </header>

      {error && <Notice tone="alert">{error}</Notice>}
      {info && !error && <Notice>{info}</Notice>}

      <PositionEntry
        lat={lat}
        lon={lon}
        onChange={(nextLat, nextLon, source) => {
          setLat(nextLat)
          setLon(nextLon)
          setError(null)
          // A hand-typed or camera-read position is not tied to whatever the
          // map had selected, so drop the pre-selected anchorage and let the
          // snap lookup below decide again.
          setInfo(
            source === 'ocr'
              ? 'Position read from the plotter. Check it against the chart before submitting.'
              : null,
          )
        }}
        onPickOnChart={() => navigate('/')}
      />

      {lat !== null && lon !== null && (
        <section className="card">
          <div className="row-between">
            <strong>Anchorage</strong>
            {checkingSnap && <span className="caption">Checking registry…</span>}
          </div>
          {anchorage?.status === 'blocked' ? (
            <Notice tone="alert">
              <strong>Not allowed — {anchorage.name ?? 'this position'}.</strong>{' '}
              {anchorage.status_reason ??
                'Anchoring here is not permitted. Talk to us before you plan around it.'}
            </Notice>
          ) : anchorage ? (
            <p className="small" style={{ marginTop: 'var(--space-2)' }}>
              {anchorage.status === 'discouraged' && (
                <>
                  <strong>We list this anchorage as discouraged.</strong>{' '}
                  {anchorage.status_reason ?? 'Check the notes before you commit to it.'}{' '}
                </>
              )}
              Filed against <strong>{anchorage.name ?? 'unnamed'}</strong>
              {anchorage.municipality ? ` · ${anchorage.municipality}` : ''} — a position we
              already have on file.
            </p>
          ) : (
            <p className="small muted" style={{ marginTop: 'var(--space-2)' }}>
              Unregistered position. We will check it with the local harbour authority; if it is
              approved, it joins the registry for next time.
            </p>
          )}
        </section>
      )}

      <section className="card stack">
        <strong>Window</strong>
        {/*
          `DateTimeField` is a bare `datetime-local` on a phone -- the native
          full-screen picker is the right control on a wet deck, and it speaks
          the captain's own phone language. On anything with a mouse it is our
          own control, because the native one draws its calendar, month names
          and format hint in the *browser's* UI language and ignores this
          document's `lang`: an app that is English everywhere else was handing
          a Norwegian picker to anyone whose Chrome is set to `nb`.
        */}
        <Field label="From" hint="Times are in this device's time zone.">
          <DateTimeField
            value={windowStart}
            onChange={setWindowStart}
            title="Start of the window you want to anchor in"
            ariaLabel="Start of the anchoring window"
            required
          />
        </Field>
        <Field label="To">
          <DateTimeField
            value={windowEnd}
            onChange={setWindowEnd}
            title="End of the window — when you expect to weigh anchor"
            ariaLabel="End of the anchoring window"
            required
          />
        </Field>
      </section>

      <section className="card stack">
        <strong>Notes for us</strong>
        <Field
          label="Anything the agent should know"
          hint="Draught, tender operations, expected arrival — anything that helps the harbour authority answer."
        >
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={4000}
            placeholder="Optional"
          />
        </Field>
      </section>

      <section className="card stack">
        <strong>Privacy</strong>
        <label className="switch">
          <input
            type="checkbox"
            checked={visible}
            onChange={(e) => {
              setVisible(e.target.checked)
              setVisibilityTouched(true)
            }}
          />
          <span>Let other vessels see this application</span>
        </label>
        <p className="caption">
          {visible
            ? 'While this is approved and your windows overlap, other captains anchoring in the same area will see your vessel name, length, window and position. They will not see your name, notes or contact details. Nobody outside that window and area sees anything.'
            : 'Hidden. No other captain will see this application at all — not your name, not an anonymous marker, nothing. Our staff can always see it.'}
        </p>
        <p className="caption">
          Vessels in a friend group always see each other, regardless of this setting.
        </p>
      </section>

      <div className="action-stack">
        <button
          type="submit"
          className="btn btn-lg btn-block"
          disabled={busy || anchorage?.status === 'blocked'}
        >
          {anchorage?.status === 'blocked' ? 'Cannot apply here' : busy ? 'Sending…' : 'Submit'}
        </button>
        <button
          type="button"
          className="btn btn-quiet btn-block"
          onClick={(e) => submit(e, true)}
          disabled={busy}
        >
          Save as draft
        </button>
      </div>
    </form>
  )
}
